// COP Copilot runtime — the deterministic Mock backend (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED from takbot services/agent-runtime/src/backends/mock.ts — the DETERMINISTIC, zero-
// dependency backend (the runtime's DEFAULT + the AUTOMATIC FALLBACK when no local /v1 server is
// reachable, so the COP/build/tests NEVER hard-depend on a running model). It implements canned
// scenarios (S1–S6) by keyword-matching the user request and emitting valid capability ids + Q1
// ASTs the COP's in-process executor (copilotGeoQuery executeQuery) knows how to run.
//
// THE ONE ADAPTATION (Q1 surface divergence, NOT a Node-ism): takbot's mock emitted
// metrics:[{op:'count'}] / group_by aggregates. The COP's reference executor (copilotGeoQuery.ts)
// is a FILTER/geo/select/order surface — it has no aggregate engine (Arquero group/metric is the
// Wave-B live worker). So the count scenarios emit a plain filtered query and DERIVE the count
// from the returned row COUNT (factsFor already falls back to payload.length). The scenario
// state-machine, keyword classifier, citations, marking roll-up, and honest-LIMITED coverage
// framing are otherwise verbatim. Every emitted query is a valid COP TableQuery.
//
// THE LOOP, per scenario: first plan() (empty transcript) → tool_calls; after the driver delivers
// the tool_results, the next plan() → {final}: a cited answer that quotes the result counts,
// carries the rollup marking, and frames coverage honestly (LIMITED).

import type { FinalAnswer, ToolCall, ToolResult } from "../types.ts";
import { CAP } from "../types.ts";
import type { Backend, Plan, SessionState } from "./types.ts";
import { pairSteps } from "./types.ts";
import { describeResult, rollUpMarking } from "../context.ts";
import type { Citation } from "../types.ts";
import type { Predicate, TableQuery } from "../../../copilotGeoQuery.ts";

/** The DDG center used by S1's radius predicate (DATA_HINT, Strait-of-Hormuz scenario). */
const DDG_CENTER: [number, number] = [56.4, 26.6];
/** Hostile fast-attack craft types (DATA_HINT). */
const FAC_TYPES = ["fast-inshore-attack-craft", "fast-attack-craft"];
/** The central strait bbox [W,S,E,N] (DATA_HINT). */
const CENTRAL_BBOX: [number, number, number, number] = [56.0, 26.0, 57.5, 27.0];
/** The weapons engagement zone ref (DATA_HINT; resolves via the visible zones map). */
const WEZ_ZONE = "wez";

type ScenarioId = "S1" | "S2" | "S3" | "S4" | "S5" | "S6" | "UNMATCHED";

/** Deterministic id minting: stable across replays of the same (turn, step). */
function callId(turnId: string, step: number): string {
  return `${turnId}-call-${step}`;
}

export class MockBackend implements Backend {
  readonly name = "mock" as const;
  readonly model = "takbot-mock-analyst";

  async plan(state: SessionState): Promise<Plan> {
    const scenario = classify(state.userText);
    const steps = pairSteps(state.transcript);
    return planScenario(scenario, state, steps);
  }

  /** Forced finalize: synthesize a grounded final from the most recent successful result. */
  async finalize(state: SessionState): Promise<FinalAnswer> {
    const steps = pairSteps(state.transcript);
    const last = [...steps].reverse().find((s) => s.result.ok);
    const marking = steps.reduce<string | undefined>(
      (m, s) => (s.result.ok ? rollUpMarking(m, describeResult(s.result.result).marking) : m),
      undefined,
    );
    if (last) {
      const d = describeResult(last.result.result);
      const final: FinalAnswer = {
        text: `Answering from the available tool results: ${d.preview.slice(0, 300)}`,
        citations: [{ ref: d.result_ref ?? last.call.call_id, index: 1, label: last.call.capability }],
        coverage_warning: "Synthesized under a forced stop; treat this as LIMITED.",
      };
      if (marking) final.marking = marking;
      return final;
    }
    return {
      text: "No grounded tool results were available to answer. This answer is LIMITED.",
      citations: [],
      coverage_warning: "No tool results; treat this as a non-answer, not a definitive negative.",
    };
  }
}

/** Keyword-match the request to a canned scenario. Order matters: WEZ-highlight (S3) before the
 *  bare hostile-filter (S2); life-safety (S6) FIRST. */
export function classify(userText: string): ScenarioId {
  const t = userText.toLowerCase();

  // S6: life-safety. Unambiguous distress words match bare; ambiguous analyst vocabulary
  // (rescue / extract / extraction) matches ONLY in a personnel/distress context.
  if (
    /\b(evac|medevac|casevac)\b|evacuat|\bman down\b|get me out|call for help|i('?m| am)\s+(hit|hurt|wounded|injured|down|trapped|bleeding)|(rescue|extract(?:ion)?)\s+(me|us|the\s+team|now|immediately)|(need|request|call for|send)\s+(?:an?\s+|immediate\s+|urgent\s+|a\s+)*(rescue|extract(?:ion)?|medevac|casevac|evac)/.test(
      t,
    )
  ) {
    return "S6";
  }

  const mentionsHostile = /hostil/.test(t);
  const mentionsFAC = /(fast.?attack|fast.?inshore|fac\b|attack craft)/.test(t);
  const mentionsDDG = /(ddg|friendly|destroyer)/.test(t);
  const mentionsWithin = /(within|near|close|\bof\b)/.test(t);
  const mentionsKm = /(km|kilomet|20\s?000|radius)/.test(t);
  const mentionsWEZ = /(weapons engagement|wez|engagement zone)/.test(t);

  if (
    mentionsFAC &&
    (mentionsDDG || mentionsHostile) &&
    (mentionsKm || mentionsWithin) &&
    /(how many|count|number)/.test(t)
  ) {
    return "S1";
  }
  if (mentionsWEZ && /(highlight|inside|in the)/.test(t)) {
    return "S3";
  }
  if (/(count|how many|number)/.test(t) && /by affiliation|affiliation/.test(t)) {
    return "S5";
  }
  if (/(zoom|pan|move|fit|center)/.test(t) && /(strait|central|map)/.test(t)) {
    return "S4";
  }
  if (mentionsHostile && /(show|only|filter|display)/.test(t)) {
    return "S2";
  }
  if (mentionsFAC && mentionsDDG && (mentionsKm || mentionsWithin)) {
    return "S1";
  }
  return "UNMATCHED";
}

/** Drive one scenario's state machine: tool_calls on the first plan, final once results are in. */
function planScenario(
  scenario: ScenarioId,
  state: SessionState,
  steps: Array<{ call: ToolCall; result: ToolResult }>,
): Plan {
  const turnId = state.turn_id;
  const done = steps.length;

  switch (scenario) {
    case "S1":
      return done === 0 ? toolCalls([s1Query(turnId)]) : { kind: "final", final: s1Final(steps) };

    case "S2":
      return done === 0 ? toolCalls([s2Filter(turnId)]) : { kind: "final", final: s2Final(steps) };

    case "S3":
      if (done === 0) return toolCalls([s3Query(turnId)]);
      if (done === 1) return toolCalls([s3Highlight(turnId, steps)]);
      return { kind: "final", final: s3Final(steps) };

    case "S4":
      return done === 0 ? toolCalls([s4Viewport(turnId)]) : { kind: "final", final: s4Final(steps) };

    case "S5":
      return done === 0 ? toolCalls([s5Query(turnId)]) : { kind: "final", final: s5Final(steps) };

    case "S6":
      return done === 0 ? toolCalls([s6Evac(turnId)]) : { kind: "final", final: s6Final(steps) };

    case "UNMATCHED":
    default:
      if (done === 0) return toolCalls([unmatchedManifest(turnId)]);
      if (done === 1) return toolCalls([unmatchedQuery(turnId)]);
      return { kind: "final", final: unmatchedFinal(state.userText, steps) };
  }
}

// ---- helpers ----------------------------------------------------------------

function toolCalls(calls: ToolCall[]): Plan {
  return { kind: "tool_calls", tool_calls: calls };
}

function mkCall(
  turnId: string,
  step: number,
  capability: string,
  reason: string,
  args: unknown,
): ToolCall {
  return { call_id: callId(turnId, step), capability, reason, args };
}

/** Pull (count, marking, ref, rows) out of a step's result for a final answer. The COP executor
 *  returns a row set (no aggregate metric), so the count is the row COUNT. */
function factsFor(step: { call: ToolCall; result: ToolResult } | undefined): {
  count?: number;
  marking?: string;
  ref?: string;
  rows?: unknown[];
} {
  if (!step || !step.result.ok) return {};
  const d = describeResult(step.result.result);
  const r = step.result.result as Record<string, unknown> | null;
  let count: number | undefined;
  let rows: unknown[] | undefined;
  if (r && typeof r === "object") {
    const rowsOf = (v: unknown): unknown[] | undefined => {
      if (Array.isArray(v)) return v;
      if (v && typeof v === "object" && Array.isArray((v as Record<string, unknown>).rows)) {
        return (v as { rows: unknown[] }).rows;
      }
      return undefined;
    };
    const payload = rowsOf(r.rows) ?? rowsOf(r.preview) ?? rowsOf(r.result) ?? rowsOf(r.value);
    if (Array.isArray(payload)) {
      rows = payload;
      count = payload.length;
    }
    if (typeof r.count === "number") count = r.count;
    if (typeof r.rows_returned === "number") count = r.rows_returned;
  }
  return { count, marking: d.marking, ref: d.result_ref, rows };
}

/** Session high-water-mark marking across the completed steps. */
function sessionMarking(steps: Array<{ call: ToolCall; result: ToolResult }>): string | undefined {
  let hwm: string | undefined;
  for (const s of steps) {
    if (s.result.ok) hwm = rollUpMarking(hwm, describeResult(s.result.result).marking);
  }
  return hwm;
}

function citationsFromSteps(steps: Array<{ call: ToolCall; result: ToolResult }>): Citation[] {
  return steps.map((s, i) => {
    const ref = s.result.ok ? describeResult(s.result.result).result_ref : undefined;
    return { ref: ref ?? s.call.call_id, index: i + 1, label: s.call.capability };
  });
}

const COVERAGE =
  "Coverage is LIMITED: these are pre-positioned map candidates filtered by clearance and need-to-know, not a canonical census — higher-fidelity or higher-classification reporting may exist that is not visible here.";

// ---- S1 (count hostile FAC within 20 km of the friendly DDG) ----------------

function s1Query(turnId: string): ToolCall {
  const where: Predicate[] = [
    { op: "radius", field: "geometry", center: DDG_CENTER, meters: 20000 },
    { field: "affiliation", op: "=", value: "hostile" },
    { field: "type", op: "in", value: FAC_TYPES },
  ];
  const query: TableQuery = {
    from: "entities",
    where,
    select: ["id", "name", "type", "classification"],
    output: { mode: "result_ref", preview_rows: 20 },
  };
  return mkCall(
    turnId,
    1,
    CAP.TABLE_QUERY,
    "Find hostile fast-attack craft within 20 km of the friendly DDG (count = returned rows).",
    query,
  );
}

function s1Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const f = factsFor(steps[0]);
  const marking = sessionMarking(steps);
  const n = f.count ?? 0;
  return {
    text:
      `There are ${n} hostile fast-attack craft within 20 km of the friendly DDG [1]` +
      `${marking ? `, at a classification roll-up of ${marking}` : ""}. ` +
      `Confidence: high (the count is the row count over the queried set). ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}

// ---- S2 (show only hostile entities) ----------------------------------------

function s2Filter(turnId: string): ToolCall {
  return mkCall(turnId, 1, CAP.MAPS_FILTER_LAYER, "Show only the hostile entities on the map.", {
    layer_id: "entities",
    predicate: { field: "affiliation", op: "=", value: "hostile" } as Predicate,
  });
}

function s2Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const marking = sessionMarking(steps);
  return {
    text:
      `The map now shows only the hostile entities (affiliation = "hostile") [1]` +
      `${marking ? `; visible roll-up ${marking}` : ""}. ` +
      `Confidence: high (a deterministic layer filter). ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}

// ---- S3 (highlight hostiles inside the WEZ) ---------------------------------

function s3Query(turnId: string): ToolCall {
  const where: Predicate[] = [
    { field: "affiliation", op: "=", value: "hostile" },
    { op: "within", field: "geometry", zone_ref: WEZ_ZONE },
  ];
  const query: TableQuery = {
    from: "entities",
    where,
    select: ["id", "name", "type"],
    output: { mode: "result_ref" },
  };
  return mkCall(
    turnId,
    1,
    CAP.TABLE_QUERY,
    "Find the hostile entities inside the weapons engagement zone (by zone_ref).",
    query,
  );
}

function s3Highlight(turnId: string, steps: Array<{ call: ToolCall; result: ToolResult }>): ToolCall {
  const ref = factsFor(steps[0]).ref;
  return mkCall(turnId, 2, CAP.MAPS_HIGHLIGHT_ROWS, "Highlight the hostiles found inside the WEZ.", {
    result_ref: ref,
    id_column: "id",
    label: "Hostiles in WEZ",
  });
}

function s3Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const q = factsFor(steps[0]);
  const marking = sessionMarking(steps);
  const n = q.count ?? (q.rows ? q.rows.length : 0);
  return {
    text:
      `Highlighted ${n} hostile entit${n === 1 ? "y" : "ies"} inside the weapons engagement zone ` +
      `and labelled them "Hostiles in WEZ" [1][2]` +
      `${marking ? `, roll-up ${marking}` : ""}. ` +
      `Confidence: high (a zone-scoped query feeding a map highlight). ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}

// ---- S4 (zoom to central strait) --------------------------------------------

function s4Viewport(turnId: string): ToolCall {
  return mkCall(turnId, 1, CAP.MAPS_SET_VIEWPORT, "Zoom the map to the central strait bbox.", {
    bbox: CENTRAL_BBOX,
  });
}

function s4Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const marking = sessionMarking(steps);
  return {
    text:
      `Moved the map camera to the central strait (bbox [${CENTRAL_BBOX.join(", ")}]) [1]. ` +
      `Confidence: high (a deterministic viewport move). ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}

// ---- S5 (entities by affiliation) -------------------------------------------
// The COP executor has no group_by aggregate, so S5 previews the entity rows and the final
// summarizes the affiliation breakdown from the returned rows.

function s5Query(turnId: string): ToolCall {
  const query: TableQuery = {
    from: "entities",
    select: ["id", "affiliation"],
    output: { mode: "preview" },
  };
  return mkCall(turnId, 1, CAP.TABLE_QUERY, "List entities with affiliation to break down counts.", query);
}

function s5Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const f = factsFor(steps[0]);
  const marking = sessionMarking(steps);
  const breakdown = renderBreakdown(f.rows);
  return {
    text:
      `Entity counts by affiliation [1]: ${breakdown}` +
      `${marking ? `. Roll-up ${marking}` : ""}. ` +
      `Confidence: high (a tally over the returned rows). ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}

/** Tally a row set by `affiliation` → "hostile=12, friend=9, …" (the COP has no group_by). */
function renderBreakdown(rows: unknown[] | undefined): string {
  if (!rows || rows.length === 0) return "(no rows returned)";
  const tally = new Map<string, number>();
  for (const row of rows) {
    if (row && typeof row === "object") {
      const r = row as Record<string, unknown>;
      const key = typeof r.affiliation === "string" ? r.affiliation : "(unknown)";
      tally.set(key, (tally.get(key) ?? 0) + 1);
    }
  }
  if (tally.size === 0) return "(unrecognized rows)";
  return [...tally.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => `${k}=${n}`).join(", ");
}

// ---- S6 (life-safety: call for evac) ----------------------------------------

function s6Evac(turnId: string): ToolCall {
  // NO coordinates — the host resolves the position from the host-owned self-location.
  return mkCall(
    turnId,
    1,
    CAP.MAPS_REQUEST_EVAC,
    "User is calling for evac; request evacuation at their host-supplied position.",
    { priority: "urgent" },
  );
}

function s6Final(steps: Array<{ call: ToolCall; result: ToolResult }>): FinalAnswer {
  const ok = steps[0]?.result.ok === true;
  return {
    text: ok
      ? `EVAC REQUESTED at your current position, marked URGENT [1]. The intent has been surfaced to the host ` +
        `(human-gated). Hold your position if safe. Confidence: high (the request was acknowledged).`
      : `I surfaced an evac request but it was not acknowledged [1]. Try again, or use the manual ` +
        `Request EVAC control. This answer is LIMITED.`,
    citations: citationsFromSteps(steps),
    coverage_warning:
      "This is a demo bench: the evac intent is human-gated and local to this map; it does NOT dispatch a real rescue.",
  };
}

// ---- UNMATCHED --------------------------------------------------------------

function unmatchedManifest(turnId: string): ToolCall {
  return mkCall(turnId, 1, CAP.TABLE_MANIFEST, "Discover the available tables and columns for a best-effort answer.", {});
}

function unmatchedQuery(turnId: string): ToolCall {
  const query: TableQuery = {
    from: "entities",
    select: ["id", "name", "affiliation", "type"],
    limit: 20,
    output: { mode: "preview", preview_rows: 20 },
  };
  return mkCall(turnId, 2, CAP.TABLE_QUERY, "Best-effort preview of the entities table to address the request.", query);
}

function unmatchedFinal(
  userText: string,
  steps: Array<{ call: ToolCall; result: ToolResult }>,
): FinalAnswer {
  const marking = sessionMarking(steps);
  const f = steps.length > 1 ? factsFor(steps[1]) : {};
  const n = f.rows ? f.rows.length : 0;
  return {
    text:
      `I could not map "${userText}" to a known scenario, so I inspected the table manifest [1] and ` +
      `previewed the entities table (${n} row${n === 1 ? "" : "s"} sampled) [2]` +
      `${marking ? `, roll-up ${marking}` : ""}. ` +
      `Confidence: low. This answer is LIMITED — a more specific request (e.g. an affiliation, a zone, or a radius) ` +
      `would let me run a targeted query. ${COVERAGE}`,
    citations: citationsFromSteps(steps),
    marking,
    coverage_warning: COVERAGE,
  };
}
